<script>
import { onMounted } from "vue";
import { useCourseFetchStore } from "@/stores/course/course-fetch-store.js";
import { useCourseDataStore } from "@/stores/course/course-data-store.js";
import { useSharedStore } from "@/stores/shared-store";
import { useAuthStore } from "@/stores/auth-store";
import CourseCard from "@/components/CustomComponent/shared/CourseCard.vue";

export default {
  components: {
    CourseCard,
  },
  setup() {
    const sharedStore = useSharedStore();
    const authStore = useAuthStore();
    const courseFetchStore = useCourseFetchStore();
    const courseDataStore = useCourseDataStore();

    onMounted(() => {
      courseFetchStore.fetchCourseList();
      authStore.authUser();
    });
    return {
      sharedStore,
      authStore,
      courseFetchStore,
      courseDataStore,
    };
  },
  methods: {
    goToPage(page) {
      if (page >= 1 && page <= this.courseFetchStore.totalPages) {
        this.courseFetchStore.pagination.currentPage = page;
      }
    },
    getPageNumbers(current, total) {
      const pages = [];

      if (total <= 5) {
        for (let i = 1; i <= total; i++) pages.push(i);
      } else {
        if (current <= 3) {
          pages.push(1, 2, 3, 4, 5);
        } else if (current >= total - 2) {
          for (let i = total - 4; i <= total; i++) pages.push(i);
        } else {
          pages.push(
            current - 2,
            current - 1,
            current,
            current + 1,
            current + 2
          );
        }
      }

      return pages;
    },
    clearFilters() {
      this.courseFetchStore.clearAllFilters();
    },
  },
};
</script>

<template>
  <layouts-header></layouts-header>

  <!-- Breadcrumb -->
  <div class="breadcrumb-bar text-center">
    <div class="container">
      <div class="row">
        <div class="col-md-12 col-12">
          <h2 class="breadcrumb-title mb-2">Course</h2>
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb justify-content-center mb-0">
              <li class="breadcrumb-item">
                <router-link to="/home">Home</router-link>
              </li>
              <li class="breadcrumb-item active" aria-current="page">Course</li>
            </ol>
          </nav>
        </div>
      </div>
    </div>
  </div>
  <!-- /Breadcrumb -->

  <!-- Course -->
  <section class="course-content">
    <div class="container">
      <div class="row align-items-baseline">
        <course-sidebar></course-sidebar>
        <div class="col-lg-9">
          <!-- Filter toolbar -->
          <div class="showing-list mb-4">
            <div class="row align-items-center g-3">
              <div class="col-lg-5">
                <div class="show-result text-center text-lg-start">
                  <h6 class="fw-medium mb-0">
                    <template v-if="courseFetchStore.filteredCount === 0">
                      No courses found
                    </template>
                    <template v-else>
                      Showing
                      {{ courseFetchStore.paginatedCourses.length }}
                      of
                      {{ courseFetchStore.filteredCount }}
                      courses
                      <span
                        v-if="courseFetchStore.totalPages > 1"
                        class="text-muted fw-normal"
                      >
                        · Page
                        {{ courseFetchStore.pagination.currentPage }}
                        of
                        {{ courseFetchStore.totalPages }}
                      </span>
                    </template>
                  </h6>
                </div>
              </div>
              <div class="col-lg-7">
                <div class="show-filter add-course-info">
                  <form action="#" @submit.prevent>
                    <div
                      class="d-sm-flex justify-content-center justify-content-lg-end align-items-center gap-2 mb-1 mb-lg-0"
                    >
                      <div
                        class="view-icons mb-2 mb-sm-0"
                        role="group"
                        aria-label="Course view mode"
                      >
                        <router-link
                          to="/course/course-grid"
                          class="grid-view active"
                          aria-label="Grid view"
                          aria-current="page"
                        >
                          <i class="feather-grid" aria-hidden="true"></i>
                        </router-link>
                        <router-link
                          to="/course/course-list"
                          class="list-view"
                          aria-label="List view"
                        >
                          <i class="isax isax-task" aria-hidden="true"></i>
                        </router-link>
                      </div>
                      <div class="search-group flex-grow-1">
                        <label class="visually-hidden" for="course-grid-search">
                          Search courses
                        </label>
                        <i
                          class="isax isax-search-normal-1"
                          aria-hidden="true"
                        ></i>
                        <input
                          id="course-grid-search"
                          type="search"
                          class="form-control"
                          placeholder="Search courses"
                          autocomplete="off"
                          :value="courseFetchStore.searchKeyword"
                          @input="
                            courseFetchStore.setSearchKeyword(
                              $event.target.value
                            )
                          "
                        />
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
          <!-- /Filter toolbar -->

          <div class="row g-3">
            <div
              v-if="courseFetchStore.paginatedCourses.length === 0"
              class="col-12"
            >
              <div class="text-center py-5 px-3">
                <span
                  class="icon-box bg-secondary-transparent d-inline-flex align-items-center justify-content-center mb-3"
                >
                  <i class="isax isax-search-status-1 fs-24" aria-hidden="true"></i>
                </span>
                <h6 class="fw-bold mb-1">
                  {{
                    courseFetchStore.hasActiveFilters
                      ? "No courses match your filters"
                      : "No courses available"
                  }}
                </h6>
                <p class="text-muted fs-14 mb-3">
                  {{
                    courseFetchStore.hasActiveFilters
                      ? "Try another keyword, or clear filters to see all courses."
                      : "Check back later for newly published courses."
                  }}
                </p>
                <button
                  v-if="courseFetchStore.hasActiveFilters"
                  type="button"
                  class="btn btn-sm btn-secondary"
                  @click="clearFilters"
                >
                  Clear filters
                </button>
              </div>
            </div>
            <div
              class="col-xl-4 col-md-6"
              v-for="(item, index) in courseFetchStore.paginatedCourses"
              :key="item.course_id || index"
            >
              <CourseCard :course="item" :status="item.status" />
            </div>
          </div>

          <!-- Pagination -->
          <nav
            v-if="courseFetchStore.totalPages > 1"
            class="row align-items-center mt-4"
            aria-label="Course pagination"
          >
            <div class="col-md-12">
              <ul
                class="pagination lms-page justify-content-center justify-content-md-end mt-2 mt-md-0 mb-0"
              >
                <li
                  class="page-item prev"
                  :class="{
                    disabled: courseFetchStore.pagination.currentPage === 1,
                  }"
                >
                  <a
                    class="page-link"
                    href="javascript:void(0)"
                    tabindex="-1"
                    aria-label="Previous page"
                    @click="
                      goToPage(courseFetchStore.pagination.currentPage - 1)
                    "
                  >
                    <i class="fas fa-angle-left" aria-hidden="true"></i>
                  </a>
                </li>

                <li
                  v-if="
                    courseFetchStore.totalPages > 5 &&
                    courseFetchStore.pagination.currentPage > 3
                  "
                  class="page-item"
                >
                  <a
                    class="page-link"
                    href="javascript:void(0)"
                    @click="goToPage(1)"
                    >1</a
                  >
                </li>
                <li
                  v-if="
                    courseFetchStore.totalPages > 5 &&
                    courseFetchStore.pagination.currentPage > 4
                  "
                  class="page-item disabled"
                >
                  <span class="page-link">...</span>
                </li>

                <li
                  v-for="page in getPageNumbers(
                    courseFetchStore.pagination.currentPage,
                    courseFetchStore.totalPages
                  )"
                  :key="page"
                  class="page-item"
                  :class="{
                    active: courseFetchStore.pagination.currentPage === page,
                  }"
                >
                  <a
                    class="page-link"
                    href="javascript:void(0)"
                    :aria-label="`Page ${page}`"
                    :aria-current="
                      courseFetchStore.pagination.currentPage === page
                        ? 'page'
                        : undefined
                    "
                    @click="goToPage(page)"
                    >{{ page }}</a
                  >
                </li>

                <li
                  v-if="
                    courseFetchStore.totalPages > 5 &&
                    courseFetchStore.pagination.currentPage <
                      courseFetchStore.totalPages - 3
                  "
                  class="page-item disabled"
                >
                  <span class="page-link">...</span>
                </li>
                <li
                  v-if="
                    courseFetchStore.totalPages > 5 &&
                    courseFetchStore.pagination.currentPage <
                      courseFetchStore.totalPages - 2
                  "
                  class="page-item"
                >
                  <a
                    class="page-link"
                    href="javascript:void(0)"
                    @click="goToPage(courseFetchStore.totalPages)"
                  >
                    {{ courseFetchStore.totalPages }}
                  </a>
                </li>

                <li
                  class="page-item next"
                  :class="{
                    disabled:
                      courseFetchStore.pagination.currentPage ===
                      courseFetchStore.totalPages,
                  }"
                >
                  <a
                    class="page-link"
                    href="javascript:void(0)"
                    aria-label="Next page"
                    @click="
                      goToPage(courseFetchStore.pagination.currentPage + 1)
                    "
                  >
                    <i class="fas fa-angle-right" aria-hidden="true"></i>
                  </a>
                </li>
              </ul>
            </div>
          </nav>
          <!-- /Pagination -->
        </div>
      </div>
    </div>
  </section>
  <!-- /Course -->
</template>
